import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Module {
  id: string;
  name: string;
  category: 'IDENTIFY' | 'PROTECT' | 'DETECT' | 'RESPOND' | 'RECOVER';
  stakeholders: {
    [stakeholderId: string]: {
      selected: boolean;
      cooperationLevel: number; // 0-100
    };
  };
}

export interface Student {
  id: string;
  name: string;
  email: string;
  currentModule: string;
  progress: number;
  activeConversation: boolean;
  conversationId?: string;
  trustLevel: number;
  gapsIdentified: number;
  lastActivity: Date;
}

export interface InstructorState {
  modules: Module[];
  students: Student[];
  selectedStudent: Student | null;
  viewingConversation: boolean;
  realtimeUpdates: {
    studentId: string;
    type: 'progress' | 'conversation' | 'gap_discovered';
    data: any;
    timestamp: Date;
  }[];
}

const initialModules: Module[] = [
  {
    id: 'identify-1',
    name: 'Asset Management',
    category: 'IDENTIFY',
    stakeholders: {}
  },
  {
    id: 'protect-1',
    name: 'Access Control',
    category: 'PROTECT',
    stakeholders: {}
  },
  {
    id: 'protect-2',
    name: 'Data Security',
    category: 'PROTECT',
    stakeholders: {}
  },
  {
    id: 'detect-1',
    name: 'Anomalies & Events',
    category: 'DETECT',
    stakeholders: {}
  },
  {
    id: 'detect-2',
    name: 'Security Monitoring',
    category: 'DETECT',
    stakeholders: {}
  },
  {
    id: 'respond-1',
    name: 'Incident Response',
    category: 'RESPOND',
    stakeholders: {}
  },
  {
    id: 'recover-1',
    name: 'Recovery Planning',
    category: 'RECOVER',
    stakeholders: {}
  }
];

const initialState: InstructorState = {
  modules: initialModules,
  students: [],
  selectedStudent: null,
  viewingConversation: false,
  realtimeUpdates: []
};

const instructorSlice = createSlice({
  name: 'instructor',
  initialState,
  reducers: {
    updateModuleStakeholder: (
      state,
      action: PayloadAction<{
        moduleId: string;
        stakeholderId: string;
        selected?: boolean;
        cooperationLevel?: number;
      }>
    ) => {
      const module = state.modules.find(m => m.id === action.payload.moduleId);
      if (module) {
        if (!module.stakeholders[action.payload.stakeholderId]) {
          module.stakeholders[action.payload.stakeholderId] = {
            selected: false,
            cooperationLevel: 50
          };
        }
        if (action.payload.selected !== undefined) {
          module.stakeholders[action.payload.stakeholderId].selected = action.payload.selected;
        }
        if (action.payload.cooperationLevel !== undefined) {
          module.stakeholders[action.payload.stakeholderId].cooperationLevel = action.payload.cooperationLevel;
        }
      }
    },
    setStudents: (state, action: PayloadAction<Student[]>) => {
      state.students = action.payload;
    },
    updateStudent: (state, action: PayloadAction<Partial<Student> & { id: string }>) => {
      const index = state.students.findIndex(s => s.id === action.payload.id);
      if (index !== -1) {
        state.students[index] = { ...state.students[index], ...action.payload };
      }
    },
    selectStudent: (state, action: PayloadAction<Student | null>) => {
      state.selectedStudent = action.payload;
    },
    setViewingConversation: (state, action: PayloadAction<boolean>) => {
      state.viewingConversation = action.payload;
    },
    addRealtimeUpdate: (state, action: PayloadAction<{
      studentId: string;
      type: 'progress' | 'conversation' | 'gap_discovered';
      data: any;
    }>) => {
      state.realtimeUpdates.push({
        ...action.payload,
        timestamp: new Date()
      });
      // Keep only last 100 updates
      if (state.realtimeUpdates.length > 100) {
        state.realtimeUpdates = state.realtimeUpdates.slice(-100);
      }
    },
    clearRealtimeUpdates: (state) => {
      state.realtimeUpdates = [];
    }
  }
});

export const {
  updateModuleStakeholder,
  setStudents,
  updateStudent,
  selectStudent,
  setViewingConversation,
  addRealtimeUpdate,
  clearRealtimeUpdates
} = instructorSlice.actions;

export default instructorSlice.reducer;